import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogBody, DialogFooter } from '@/components/ui/dialog';
import { PageBody, PageHeader } from '@/components/AppShell';
import { SortHeader, makeToggleSort, cmp, type SortState } from '@/components/ui/sort-header';
import { api, ApiError, mfaApi } from '@/lib/api';
import { useAuthStore, type Role } from '@/stores/auth.store';

interface UserRow {
  id: string;
  name: string;
  email: string;
  role: Role;
  active: boolean;
  lastLoginAt: string | null;
  mfaEnabled: boolean;
  createdAt: string;
}

const createSchema = z.object({
  name: z.string().min(2, 'Nome muito curto'),
  email: z.string().email('E-mail inválido'),
  password: z.string().min(12, 'Senha deve ter ao menos 12 caracteres'),
  role: z.enum(['ADMIN', 'OPERATOR']),
});
type CreateValues = z.infer<typeof createSchema>;

const editSchema = z.object({
  name: z.string().min(2, 'Nome muito curto'),
  email: z.string().email('E-mail inválido'),
  role: z.enum(['ADMIN', 'OPERATOR']),
  active: z.boolean(),
});
type EditValues = z.infer<typeof editSchema>;

const passwordSchema = z
  .object({
    newPassword: z.string().min(12, 'Senha deve ter ao menos 12 caracteres'),
    confirmPassword: z.string(),
  })
  .refine((d) => d.newPassword === d.confirmPassword, {
    message: 'As senhas não conferem',
    path: ['confirmPassword'],
  });
type PasswordValues = z.infer<typeof passwordSchema>;

const roleOrder: Record<Role, number> = { ADMIN: 0, OPERATOR: 1 };

type UsersSortColumn = 'name' | 'email' | 'role' | 'active' | 'lastLoginAt';

export function UsersPage() {
  const me = useAuthStore((s) => s.user);
  const qc = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<UserRow | null>(null);
  const [changingPassword, setChangingPassword] = useState<UserRow | null>(null);
  const [deleting, setDeleting] = useState<UserRow | null>(null);
  const [sort, setSort] = useState<SortState<UsersSortColumn>>({ column: 'name', dir: 'asc' });
  const toggleSort = makeToggleSort(setSort);

  const { data, isLoading } = useQuery({
    queryKey: ['users'],
    queryFn: () => api<UserRow[]>('/users'),
  });

  const sortedUsers = useMemo(() => {
    const rows = data ?? [];
    const factor = sort.dir === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
      let primary = 0;
      switch (sort.column) {
        case 'name':        primary = cmp.text(a.name, b.name); break;
        case 'email':       primary = cmp.text(a.email, b.email); break;
        case 'role':        primary = roleOrder[a.role] - roleOrder[b.role]; break;
        case 'active':      primary = cmp.bool(a.active, b.active); break;
        case 'lastLoginAt': primary = cmp.date(a.lastLoginAt, b.lastLoginAt); break;
      }
      if (primary !== 0) return primary * factor;
      return cmp.text(a.name, b.name);
    });
  }, [data, sort]);

  const createMut = useMutation({
    mutationFn: (values: CreateValues) =>
      api<UserRow>('/users', { method: 'POST', body: values }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['users'] });
      setCreating(false);
    },
  });

  const resetMfaMut = useMutation({
    mutationFn: (id: string) => mfaApi.adminReset(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  });

  return (
    <>
      <PageHeader
        title="Usuários"
        breadcrumb="Configurações"
        actions={
          <Button onClick={() => setCreating(true)} size="sm">
            + Novo usuário
          </Button>
        }
      />
      <PageBody>
        {creating && <CreateUserForm onCancel={() => setCreating(false)} mutation={createMut} />}

        <div className="bg-card border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-left text-muted-foreground">
              <tr>
                <SortHeader column="name"        label="Nome"          sort={sort} onClick={toggleSort} />
                <SortHeader column="email"       label="E-mail"        sort={sort} onClick={toggleSort} />
                <SortHeader column="role"        label="Perfil"        sort={sort} onClick={toggleSort} />
                <SortHeader column="active"      label="Status"        sort={sort} onClick={toggleSort} />
                <th className="px-4 py-2 font-medium">MFA</th>
                <SortHeader column="lastLoginAt" label="Último login"  sort={sort} onClick={toggleSort} />
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {isLoading && (
                <tr>
                  <td colSpan={7} className="px-4 py-6 text-muted-foreground text-center">
                    Carregando…
                  </td>
                </tr>
              )}
              {sortedUsers.map((u) => (
                <tr key={u.id}>
                  <td className="px-4 py-3">
                    {u.name}
                    {u.id === me?.id && (
                      <span className="ml-2 text-xs text-muted-foreground">(você)</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">{u.email}</td>
                  <td className="px-4 py-3">
                    <span
                      className={
                        u.role === 'ADMIN'
                          ? 'text-xs bg-indigo-100 text-indigo-800 px-2 py-0.5 rounded'
                          : 'text-xs bg-muted text-muted-foreground px-2 py-0.5 rounded'
                      }
                    >
                      {u.role === 'ADMIN' ? 'Administrador' : 'Operador'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {u.active ? (
                      <span className="text-xs bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded">
                        Ativo
                      </span>
                    ) : (
                      <span className="text-xs bg-rose-100 text-rose-800 px-2 py-0.5 rounded">
                        Inativo
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {u.mfaEnabled ? (
                      <span className="text-xs bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded">
                        Ativo
                      </span>
                    ) : (
                      <span className="text-xs bg-muted text-muted-foreground px-2 py-0.5 rounded">
                        Inativo
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">
                    {u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleString('pt-BR') : '—'}
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button
                      onClick={() => setEditing(u)}
                      className="text-xs text-indigo-600 hover:underline mr-3"
                    >
                      Editar
                    </button>
                    <button
                      onClick={() => setChangingPassword(u)}
                      className="text-xs text-indigo-600 hover:underline mr-3"
                    >
                      Senha
                    </button>
                    {u.mfaEnabled && (
                      <button
                        onClick={() => {
                          if (
                            window.confirm(
                              `Resetar o MFA de ${u.name}? O usuário precisará configurar novamente a verificação em duas etapas.`,
                            )
                          ) {
                            resetMfaMut.mutate(u.id);
                          }
                        }}
                        disabled={resetMfaMut.isPending}
                        className="text-xs text-amber-600 hover:underline mr-3 disabled:opacity-50"
                      >
                        Reset MFA
                      </button>
                    )}
                    {u.id !== me?.id && (
                      <button
                        onClick={() => setDeleting(u)}
                        className="text-xs text-rose-600 hover:underline"
                      >
                        Apagar
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </PageBody>

      {editing && (
        <EditUserDialog
          user={editing}
          onClose={() => setEditing(null)}
          isSelf={editing.id === me?.id}
        />
      )}
      {changingPassword && (
        <ChangePasswordDialog
          user={changingPassword}
          onClose={() => setChangingPassword(null)}
        />
      )}
      {deleting && (
        <DeleteUserDialog user={deleting} onClose={() => setDeleting(null)} />
      )}
    </>
  );
}

interface CreateProps {
  onCancel: () => void;
  mutation: {
    mutate: (v: CreateValues) => void;
    isPending: boolean;
    error: unknown;
  };
}

function CreateUserForm({ onCancel, mutation }: CreateProps) {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<CreateValues>({
    resolver: zodResolver(createSchema),
    defaultValues: { role: 'OPERATOR' },
  });

  const apiError =
    mutation.error instanceof ApiError ? mutation.error.message : null;

  return (
    <form
      onSubmit={handleSubmit((v) => mutation.mutate(v))}
      className="bg-card border rounded-lg p-4 grid grid-cols-2 gap-3"
    >
      <div className="col-span-2">
        <h3 className="font-medium">Novo usuário</h3>
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="name">Nome</Label>
        <Input id="name" {...register('name')} />
        {errors.name && <p className="text-xs text-destructive">{errors.name.message}</p>}
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="email">E-mail</Label>
        <Input id="email" type="email" {...register('email')} />
        {errors.email && <p className="text-xs text-destructive">{errors.email.message}</p>}
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="password">Senha (≥ 12 caracteres)</Label>
        <Input id="password" type="password" {...register('password')} />
        {errors.password && (
          <p className="text-xs text-destructive">{errors.password.message}</p>
        )}
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="role">Perfil</Label>
        <select
          id="role"
          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          {...register('role')}
        >
          <option value="OPERATOR">Operador</option>
          <option value="ADMIN">Administrador</option>
        </select>
      </div>
      {apiError && (
        <div className="col-span-2 text-sm text-destructive bg-destructive/10 border border-destructive/30 rounded-md p-2">
          {apiError}
        </div>
      )}
      <div className="col-span-2 flex justify-end gap-2 pt-2">
        <Button type="button" variant="outline" onClick={onCancel} disabled={mutation.isPending}>
          Cancelar
        </Button>
        <Button type="submit" disabled={mutation.isPending}>
          {mutation.isPending ? 'Criando…' : 'Criar usuário'}
        </Button>
      </div>
    </form>
  );
}

// --------- Edit dialog ---------

function EditUserDialog({
  user,
  onClose,
  isSelf,
}: {
  user: UserRow;
  onClose: () => void;
  isSelf: boolean;
}) {
  const qc = useQueryClient();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<EditValues>({
    resolver: zodResolver(editSchema),
    defaultValues: {
      name: user.name,
      email: user.email,
      role: user.role,
      active: user.active,
    },
  });

  const mut = useMutation({
    mutationFn: (values: EditValues) =>
      api<UserRow>(`/users/${user.id}`, { method: 'PATCH', body: values }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['users'] });
      onClose();
    },
  });

  const apiError = mut.error instanceof ApiError ? mut.error.message : null;

  return (
    <Dialog open onClose={onClose} title="Editar usuário">
      <form onSubmit={handleSubmit((v) => mut.mutate(v))}>
        <DialogBody className="grid grid-cols-2 gap-3">
          <div className="col-span-2 space-y-1.5">
            <Label htmlFor="edit-name">Nome</Label>
            <Input id="edit-name" {...register('name')} />
            {errors.name && <p className="text-xs text-destructive">{errors.name.message}</p>}
          </div>
          <div className="col-span-2 space-y-1.5">
            <Label htmlFor="edit-email">E-mail</Label>
            <Input id="edit-email" type="email" {...register('email')} />
            {errors.email && (
              <p className="text-xs text-destructive">{errors.email.message}</p>
            )}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="edit-role">Perfil</Label>
            <select
              id="edit-role"
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
              disabled={isSelf}
              {...register('role')}
            >
              <option value="OPERATOR">Operador</option>
              <option value="ADMIN">Administrador</option>
            </select>
            {isSelf && (
              <p className="text-xs text-muted-foreground">
                Você não pode alterar seu próprio perfil.
              </p>
            )}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="edit-active">Status</Label>
            <label className="flex h-10 items-center gap-2 rounded-md border border-input bg-background px-3 text-sm">
              <input
                id="edit-active"
                type="checkbox"
                disabled={isSelf}
                {...register('active')}
              />
              <span>Usuário ativo</span>
            </label>
            {isSelf && (
              <p className="text-xs text-muted-foreground">
                Você não pode desativar a si mesmo.
              </p>
            )}
          </div>
          {apiError && (
            <div className="col-span-2 text-sm text-destructive bg-destructive/10 border border-destructive/30 rounded-md p-2">
              {apiError}
            </div>
          )}
        </DialogBody>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose} disabled={mut.isPending}>
            Cancelar
          </Button>
          <Button type="submit" disabled={mut.isPending}>
            {mut.isPending ? 'Salvando…' : 'Salvar'}
          </Button>
        </DialogFooter>
      </form>
    </Dialog>
  );
}

// --------- Change password dialog ---------

function ChangePasswordDialog({
  user,
  onClose,
}: {
  user: UserRow;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<PasswordValues>({
    resolver: zodResolver(passwordSchema),
  });

  const mut = useMutation({
    mutationFn: (values: PasswordValues) =>
      api<{ ok: true }>(`/users/${user.id}/password`, {
        method: 'PATCH',
        body: { newPassword: values.newPassword },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['users'] });
      onClose();
    },
  });

  const apiError = mut.error instanceof ApiError ? mut.error.message : null;

  return (
    <Dialog open onClose={onClose} title={`Alterar senha — ${user.name}`}>
      <form onSubmit={handleSubmit((v) => mut.mutate(v))}>
        <DialogBody className="space-y-3">
          <p className="text-sm text-muted-foreground">
            A nova senha entra em vigor imediatamente. Sessões ativas deste usuário serão
            encerradas.
          </p>
          <div className="space-y-1.5">
            <Label htmlFor="new-password">Nova senha (≥ 12 caracteres)</Label>
            <Input id="new-password" type="password" autoComplete="new-password" {...register('newPassword')} />
            {errors.newPassword && (
              <p className="text-xs text-destructive">{errors.newPassword.message}</p>
            )}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="confirm-password">Confirmar nova senha</Label>
            <Input
              id="confirm-password"
              type="password"
              autoComplete="new-password"
              {...register('confirmPassword')}
            />
            {errors.confirmPassword && (
              <p className="text-xs text-destructive">{errors.confirmPassword.message}</p>
            )}
          </div>
          {apiError && (
            <div className="text-sm text-destructive bg-destructive/10 border border-destructive/30 rounded-md p-2">
              {apiError}
            </div>
          )}
        </DialogBody>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose} disabled={mut.isPending}>
            Cancelar
          </Button>
          <Button type="submit" disabled={mut.isPending}>
            {mut.isPending ? 'Alterando…' : 'Alterar senha'}
          </Button>
        </DialogFooter>
      </form>
    </Dialog>
  );
}

// --------- Delete dialog (deactivate / hard-delete) ---------

function DeleteUserDialog({
  user,
  onClose,
}: {
  user: UserRow;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [confirmText, setConfirmText] = useState('');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const deactivateMut = useMutation({
    mutationFn: () => api<UserRow>(`/users/${user.id}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['users'] });
      onClose();
    },
    onError: (e) => setErrorMsg(e instanceof ApiError ? e.message : 'Erro ao desativar'),
  });

  const reactivateMut = useMutation({
    mutationFn: () =>
      api<UserRow>(`/users/${user.id}`, { method: 'PATCH', body: { active: true } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['users'] });
      onClose();
    },
    onError: (e) => setErrorMsg(e instanceof ApiError ? e.message : 'Erro ao reativar'),
  });

  const hardDeleteMut = useMutation({
    mutationFn: () =>
      api<{ ok: true }>(`/users/${user.id}/permanent`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['users'] });
      onClose();
    },
    onError: (e) => setErrorMsg(e instanceof ApiError ? e.message : 'Erro ao apagar'),
  });

  const canHardDelete = confirmText.trim().toUpperCase() === 'APAGAR';
  const anyPending = deactivateMut.isPending || reactivateMut.isPending || hardDeleteMut.isPending;

  return (
    <Dialog open onClose={onClose} title={`Apagar usuário — ${user.name}`}>
      <DialogBody className="space-y-4">
        {user.active ? (
          <div className="space-y-2">
            <h4 className="text-sm font-medium">Desativar usuário</h4>
            <p className="text-sm text-muted-foreground">
              O usuário fica como <strong>Inativo</strong> e não consegue mais entrar no
              sistema, mas todo o histórico (lançamentos, auditoria) é preservado. Recomendado
              para a maioria dos casos.
            </p>
            <Button
              variant="outline"
              onClick={() => {
                setErrorMsg(null);
                deactivateMut.mutate();
              }}
              disabled={anyPending}
            >
              {deactivateMut.isPending ? 'Desativando…' : 'Desativar usuário'}
            </Button>
          </div>
        ) : (
          <div className="space-y-2">
            <h4 className="text-sm font-medium">Reativar usuário</h4>
            <p className="text-sm text-muted-foreground">
              O usuário está inativo. Reative para permitir novo login.
            </p>
            <Button
              variant="outline"
              onClick={() => {
                setErrorMsg(null);
                reactivateMut.mutate();
              }}
              disabled={anyPending}
            >
              {reactivateMut.isPending ? 'Reativando…' : 'Reativar usuário'}
            </Button>
          </div>
        )}

        <div className="border-t pt-4 space-y-2">
          <h4 className="text-sm font-medium text-rose-600">Apagar permanentemente</h4>
          <p className="text-sm text-muted-foreground">
            Remove o usuário do banco de dados. Só é possível apagar usuários que ainda não
            criaram lançamentos, contas a pagar/receber ou transferências (para preservar a
            integridade do histórico). Para os demais casos, use Desativar.
          </p>
          <p className="text-sm text-muted-foreground">
            Digite <strong>APAGAR</strong> para confirmar:
          </p>
          <Input
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder="APAGAR"
          />
          <Button
            variant="destructive"
            onClick={() => {
              setErrorMsg(null);
              hardDeleteMut.mutate();
            }}
            disabled={!canHardDelete || anyPending}
          >
            {hardDeleteMut.isPending ? 'Apagando…' : 'Apagar permanentemente'}
          </Button>
        </div>

        {errorMsg && (
          <div className="text-sm text-destructive bg-destructive/10 border border-destructive/30 rounded-md p-2">
            {errorMsg}
          </div>
        )}
      </DialogBody>
      <DialogFooter>
        <Button variant="outline" onClick={onClose} disabled={anyPending}>
          Fechar
        </Button>
      </DialogFooter>
    </Dialog>
  );
}
